import React, { useEffect, useMemo, useState } from "react";
import { Button, Offcanvas, Form } from "react-bootstrap";
import type { AdCampaignListParams, AdsPricingModel } from "../../../../api/ads";
import { AdsCampaignStatus } from "../../../../api/ads";

import "./CampaignFilterDrawer.css";

export interface CampaignFilterDrawerProps {
  show: boolean;
  onClose: () => void;
  onApply: (filters: Partial<AdCampaignListParams>) => void;
  onReset: () => void;
  initialFilters?: Partial<AdCampaignListParams>;
}

const pricingModelOptions: Array<AdsPricingModel> = ["CPC", "CPM", "Flat"];

export const CampaignFilterDrawer: React.FC<CampaignFilterDrawerProps> = ({
  show,
  onClose,
  onApply,
  onReset,
  initialFilters,
}) => {
  const defaultFilters: Partial<AdCampaignListParams> = useMemo(
    () => ({
      placement: "ALL",
      pricingModel: "ALL",
      status: "ALL",
      startAt: undefined,
      endAt: undefined,
    }),
    []
  );

  const [placement, setPlacement] = useState<string>(defaultFilters.placement as string);
  const [pricingModel, setPricingModel] = useState<AdsPricingModel | "ALL">(defaultFilters.pricingModel as any);
  const [status, setStatus] = useState<AdsCampaignStatus | "ALL">(defaultFilters.status as any);
  const [startAt, setStartAt] = useState<string>("");
  const [endAt, setEndAt] = useState<string>("");

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    const merged = { ...defaultFilters, ...(initialFilters || {}) };
    setPlacement((merged.placement as string) ?? "ALL");
    setPricingModel((merged.pricingModel as any) ?? "ALL");
    setStatus((merged.status as any) ?? "ALL");
    setStartAt(
      typeof merged.startAt === "string" ? merged.startAt.split("T")[0] : ""
    );
    setEndAt(
      typeof merged.endAt === "string" ? merged.endAt.split("T")[0] : ""
    );
    setError(null);
  }, [show, defaultFilters, initialFilters]);

  const handleApply = () => {
    setError(null);

    if (startAt && endAt) {
      const start = new Date(startAt);
      const end = new Date(endAt);
      if (end < start) {
        setError("End date cannot be earlier than start date.");
        return;
      }
    }

    const filters: Partial<AdCampaignListParams> = {
      placement: placement === "ALL" ? "ALL" : placement,
      pricingModel: pricingModel === "ALL" ? "ALL" : pricingModel,
      status: status === "ALL" ? "ALL" : status,
      startAt: startAt ? startAt : undefined,
      endAt: endAt ? endAt : undefined,
    };

    onApply(filters);
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  const placementOptions = [
    "ALL",
    "HOME_BANNER",
    "TOURNAMENT_BANNER",
    "SHOP_BANNER",
    "POPUP",
  ];

  const placementLabel = (value: string) => {
    if (value === "ALL") return "All";
    return value
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <>
      <Offcanvas show={show} onHide={onClose} placement="end" className="ads-filter-drawer">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Campaign Filters</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="ads-filter-drawer__content">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Placement</Form.Label>
                <Form.Select value={placement} onChange={(e) => setPlacement(e.target.value)}>
                  {placementOptions.map((p) => (
                    <option key={p} value={p}>
                      {placementLabel(p)}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Pricing Model</Form.Label>
                <Form.Select value={pricingModel} onChange={(e) => setPricingModel(e.target.value as any)}>
                  <option value="ALL">All</option>
                  {pricingModelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select value={status} onChange={(e) => setStatus(e.target.value as any)}>
                  <option value="ALL">All</option>
                  {Object.values(AdsCampaignStatus).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Date Range</Form.Label>
                <div className="ads-filter-drawer__date-row">
                  <Form.Control
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                  />
                  <span className="ads-filter-drawer__date-sep">to</span>
                  <Form.Control
                    type="date"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                  />
                </div>
              </Form.Group>

              {error && <div className="ads-filter-drawer__error">{error}</div>}

              <div className="ads-filter-drawer__actions">
                <Button variant="outline-secondary" className="ads-filter-drawer__btn" onClick={handleReset}>
                  Reset
                </Button>
                <Button variant="primary" className="ads-filter-drawer__btn" onClick={handleApply}>
                  Apply
                </Button>
              </div>
            </Form>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

